"use client";

import React, { useEffect, useRef, useState, useContext, useMemo } from "react";
import { useSelector } from "react-redux";
import DOMPurify from "dompurify";

import SocketContext from "@/socket/Context";
import Partials from "../partials";
import sk from "@/skeletons";
import utils from "@/utils";
import "../player-side.scss";

function DetailSection(props) {
  const { isEnrolled, videoInfo, courseInfo, isRestricted, articleInfo } =
    useSelector((state) => state.video);
  const user = useSelector((state) => state.user.users);
  const [views, setViews] = useState(0);

  const { socket } = useContext(SocketContext).SocketState;

  const { handleWarning, isLoading } = props;
  const course_about_ref = useRef(null);
  const video_about_ref = useRef(null);

  const enrolledBeforeApril = useMemo(() => {
    const AddOnCourses = [
      "67519e5f97e6f186a86c34e7", //Web Fundamentals
      "675c48e2da37aaa2615133e8", //Computer Networks
      "67945eb4f6aa8a9b3bbb9483", //Operating System
      "67e3ceca45b2b98d0db4e709", //Low level design
      "64fb4665bf30d08470ece486", //Dynamic Programming
      "680fb364438493edbbcc9b03", //GIT & GITHUB
    ];
    if (!isEnrolled) return false;
    const enrolledDate = new Date(isEnrolled.createdAt);
    const compareDate = new Date("2025-04-01T00:00:00.000Z");
    return (
      enrolledDate <= compareDate ||
      AddOnCourses.includes(isEnrolled?.courseId.toString())
    );
  }, [isEnrolled]);

  useEffect(() => {
    if (video_about_ref.current && videoInfo) {
      video_about_ref.current.innerHTML = DOMPurify.sanitize(
        utils.linkify(videoInfo?.about)
      );
    } else if (video_about_ref.current && articleInfo) {
      video_about_ref.current.innerHTML = DOMPurify.sanitize(
        utils.linkify(courseInfo?.about)
      );
    }
    if (course_about_ref.current) {
      course_about_ref.current.innerHTML = DOMPurify.sanitize(
        utils.linkify(courseInfo?.about)
      );
    }
  }, [videoInfo, courseInfo, articleInfo]);

  useEffect(() => {
    setViews(videoInfo?.views);
  }, [videoInfo]);

  useEffect(() => {
    if (videoInfo) {
      // socket listner to increase the view
      socket?.on("video:viewIncrease", (res) => {
        if (res.videoId === videoInfo._id) {
          setViews((prev) => prev + 1);
        }
      });
    }
  }, [socket, videoInfo]);

  return (
    <>
      <section className="vd-transciption-container">
        <div className="vd-btns-container">
          {user.email &&
          isEnrolled &&
          (isEnrolled?.visibility !== "restricted" || enrolledBeforeApril) ? (
            !isLoading ? (
              <Partials.CertificateReqBtn
                warningCallback={(obj) => handleWarning(obj)}
              />
            ) : (
              <sk.SkeletonElement type="sk-follow-btn" />
            )
          ) : null}
          {!isRestricted ? (
            !isLoading ? (
              <Partials.EnrollUnEnrollBtn
                warningCallback={(obj) => handleWarning(obj)}
              />
            ) : (
              <sk.SkeletonElement type="sk-follow-btn" />
            )
          ) : null}
        </div>
        <article>
          <header>
            {!isLoading ? (
              <h3>
                About this {videoInfo ? "video" : "course"} <span></span>{" "}
                {typeof views === "number" && (
                  <span className="views-text">
                    {utils.formatNumber(views)} views
                  </span>
                )}
              </h3>
            ) : (
              <sk.SkeletonElement type="sk-name-text" />
            )}
          </header>
          <div className="vd-description">
            <div ref={video_about_ref} className="vd-description-text"></div>
          </div>
          {videoInfo?.resources?.length ? (
            <div className="vd-resources">
              <p>Resources: </p>
              {videoInfo?.resources?.split(",").map((value, i) => (
                <p key={i} className="resource-details">
                  <a href={value}>{value}</a>
                </p>
              ))}
            </div>
          ) : null}
          {isLoading && <sk.RowCardSkeleton number={8} type="sk-sentence" />}
        </article>
        {!articleInfo && (
          <article>
            <header className="vd-transciption-header">
              {!isLoading ? (
                <h3>About this course</h3>
              ) : (
                <sk.SkeletonElement type="sk-name-text" />
              )}
            </header>
            <div className="vd-description">
              <div ref={course_about_ref} className="vd-description-text"></div>
            </div>
            {isLoading && <sk.RowCardSkeleton number={8} type="sk-sentence" />}
          </article>
        )}
      </section>
    </>
  );
}

export default React.memo(DetailSection);
